import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { CreateSparkActivityPayload, SparkActivityStageForm } from '../../types/spark-activity';
import { useCreateSparkActivity, useSparkActivityFilterOptions } from '../../hooks/useSparkActivities';
import { validateActivityPayload } from './validateActivityPayload';
import { buildValidationToast } from './buildValidationToast';
import ClassificationPanel from './ClassificationPanel';
import StageLadder from './StageLadder';
import UnsavedChangesModal from './UnsavedChangesModal';
import PineSelect from './PineSelect';

const DURATION_OPTIONS = [
  { label: '5–10 min', value: '5-10', duration: 5, durationMax: 10 },
  { label: '10–15 min', value: '10-15', duration: 10, durationMax: 15 },
  { label: '15–20 min', value: '15-20', duration: 15, durationMax: 20 },
  { label: '20–30 min', value: '20-30', duration: 20, durationMax: 30 },
];

function getDurationValue(duration: number, durationMax: number | null): string {
  const match = DURATION_OPTIONS.find(
    (o) => o.duration === duration && o.durationMax === durationMax
  );
  return match?.value ?? `${duration}-${durationMax ?? duration}`;
}

interface ActivityCreatePanelProps {
  formData: CreateSparkActivityPayload | null;
  setFormData: React.Dispatch<React.SetStateAction<CreateSparkActivityPayload | null>>;
  focusedStage: number | null;
  onFocusedStageChange: (index: number | null) => void;
  onTogglePreview: () => void;
  previewVisible: boolean;
  onCancel: () => void;
  onCreated: (id: number) => void;
}

export default function ActivityCreatePanel({
  formData,
  setFormData,
  focusedStage,
  onFocusedStageChange,
  onTogglePreview,
  previewVisible,
  onCancel,
  onCreated,
}: ActivityCreatePanelProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(false);
  const [imageValue, setImageValue] = useState<{ documentId: number; viewUrl: string } | null>(null);

  const createMutation = useCreateSparkActivity();
  const { data: filterOptions } = useSparkActivityFilterOptions();

  // Initialize form when communication stages are loaded
  useEffect(() => {
    if (!filterOptions?.communicationStages?.length) return;
    const stages: SparkActivityStageForm[] = filterOptions.communicationStages.map((cs, i) => ({
      communicationStageId: cs.id,
      description: '',
      points: [],
      whyItWorks: '',
      expertTips: '',
      domainId: 0,
      skillIds: [],
      active: i === 0,
    }));
    setFormData({
      title: '',
      prep: '',
      duration: 5,
      durationMax: 10,
      stages,
      playIds: [],
      preferenceIds: [],
      packIds: [],
      imageDocumentId: null,
    });
    setImageValue(null);
  }, [filterOptions?.communicationStages]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleFieldChange = (field: keyof CreateSparkActivityPayload, value: unknown) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
    setIsDirty(true);
    if (errors[field as string]) setErrors((prev) => ({ ...prev, [field as string]: undefined as unknown as string }));
  };

  const handleStageChange = (index: number, field: keyof SparkActivityStageForm, value: unknown) => {
    setFormData((prev) => {
      if (!prev) return null;
      const newStages = [...prev.stages];
      newStages[index] = { ...newStages[index], [field]: value };
      return { ...prev, stages: newStages };
    });
    setIsDirty(true);
    if (field === 'active' && value === false) {
      setErrors((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          if (key.startsWith(`stages.${index}.`)) delete next[key];
        }
        return next;
      });
    } else {
      const errorKey = `stages.${index}.${field}`;
      if (errors[errorKey]) setErrors((prev) => ({ ...prev, [errorKey]: undefined as unknown as string }));
    }
  };

  const handleSave = async () => {
    if (!formData) return;

    const payload: CreateSparkActivityPayload = {
      ...formData,
      stages: formData.stages.map((stage) => ({
        ...stage,
        points: stage.points.map(({ _id: _unused, ...p }) => p),
      })),
    };
    const validationErrors = validateActivityPayload(payload);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error(buildValidationToast(validationErrors));
      return;
    }

    try {
      const result = await createMutation.mutateAsync(payload);
      setIsDirty(false);
      toast.success('Activity created');
      onCreated(result.id);
    } catch {
      // error toast handled by mutation onError
    }
  };

  const handleCancelClick = () => {
    if (isDirty) {
      setPendingCancel(true);
    } else {
      onCancel();
    }
  };

  const handleImageChange = (image: { documentId: number; viewUrl: string } | null) => {
    setImageValue(image);
    handleFieldChange('imageDocumentId', image?.documentId ?? null);
  };

  if (!formData) {
    return (
      <div className="admin-card p-[30px] flex items-center justify-center min-h-[200px]">
        <span className="text-pine-ink-soft text-sm">Loading…</span>
      </div>
    );
  }

  const durationValue = getDurationValue(formData.duration, formData.durationMax);

  return (
    <>
      <div data-testid="activity-create-panel" className="admin-card p-[30px] relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-pine-label uppercase tracking-[0.12em] font-bold text-pine-ink-soft text-[11px]">
              New Activity
            </p>
          </div>
          <button
            onClick={onTogglePreview}
            className="p-1.5 rounded-md hover:bg-pine-hover-item transition-colors text-pine-ink-soft hover:text-pine-ink"
            title={previewVisible ? 'Hide preview' : 'Show preview'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
              <line x1="12" y1="18" x2="12.01" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Title */}
        <input
          data-testid="activity-title-input"
          type="text"
          value={formData.title}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          placeholder="Activity title"
          className={`w-full font-display text-[31px] font-semibold bg-transparent border-b-2 outline-none transition-colors
            ${errors.title ? 'border-pine-danger' : 'border-transparent hover:border-pine-line focus:border-pine-pine'}`}
        />
        {errors.title && (
          <p className="text-[11px] text-pine-danger mt-1">{errors.title}</p>
        )}

        {/* Prep + Duration */}
        <div className="grid grid-cols-2 gap-[26px] mt-[26px]">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mb-1">
              What you need / Prep
            </label>
            <input
              type="text"
              value={formData.prep}
              onChange={(e) => handleFieldChange('prep', e.target.value)}
              placeholder="e.g. No materials needed"
              className="w-full border-[1.5px] border-pine-line rounded-pine-input px-3 py-2 text-[13px] hover:border-pine-pine focus:border-pine-pine focus:outline-none bg-white transition-colors"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mb-1">
              Duration
            </label>
            <PineSelect
              options={DURATION_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
              value={durationValue}
              onChange={(v) => {
                const opt = DURATION_OPTIONS.find((o) => o.value === v);
                if (opt) {
                  handleFieldChange('duration', opt.duration);
                  handleFieldChange('durationMax', opt.durationMax);
                }
              }}
            />
          </div>
        </div>

        {/* Classification */}
        <ClassificationPanel
          formData={formData}
          onChange={handleFieldChange}
          imageValue={imageValue}
          onImageChange={handleImageChange}
        />

        {/* Stage Ladder */}
        <StageLadder
          stages={formData.stages}
          onStageChange={handleStageChange}
          errors={errors}
          focusedCol={focusedStage}
          onFocusedColChange={onFocusedStageChange}
        />

        {/* Action Bar */}
        <div className="flex items-center justify-between mt-8 pt-4 border-t border-pine-line">
          <button
            onClick={handleCancelClick}
            className="px-4 py-2 text-sm text-pine-ink-soft hover:text-pine-ink border border-pine-line rounded-md hover:bg-pine-hover-item transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={createMutation.isPending}
            className="px-5 py-2 text-sm font-semibold bg-pine-pine hover:bg-pine-pine-deep text-white rounded-md disabled:opacity-60 transition-colors"
          >
            {createMutation.isPending ? 'Creating…' : 'Create Activity'}
          </button>
        </div>
      </div>

      <UnsavedChangesModal
        open={pendingCancel}
        onLeave={() => {
          setPendingCancel(false);
          setIsDirty(false);
          onCancel();
        }}
        onStay={() => setPendingCancel(false)}
      />
    </>
  );
}
